/**
 * Página CrmGeneratedContracts - Listagem de Contratos Gerados
 * 
 * Características:
 * - Header compacto replicando CrmDeals
 * - Tabela premium com colunas de contratos
 * - Paginação server-side via RPC get_contracts_paginated
 * - Filtros avançados (workspace, status, provider, data, busca livre, etc.)
 * - Ordenação server-side por colunas
 * - Controle de visibilidade via should_apply_ownership_filter
 * 
 * @version 1.2.0
 * RELEASE: 2026-08-25 v1.2.0 - A URL é a única fonte de verdade do modo de visualização.
 *          Sem `?view=kanban` a tela sempre abre em listagem (removida a preferência
 *          persistida em localStorage), pois o kanban já possui menu próprio.
 * RELEASE: 2026-08-25 v1.1.0 - Modo de visualização (lista/kanban) passa a aceitar
 *          o parâmetro de URL `?view=kanban`, permitindo link direto do menu
 *          "Kanban de Contratos" e compartilhamento da visão.
 * RELEASE: 2026-02-20 v1.0.0 - Criação inicial
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Search,
  FileSignature,
  X,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Clock,
  Eye,
  Calendar,
  Building2,
  PlayCircle,
  UserCheck,
  Plus,
  MoreHorizontal,
  FileText,
  Send,
  XCircle,
  Download,
  Loader2,
  RotateCcw,
  Trash2,
  FileCheck,
  ExternalLink,
  SlidersHorizontal,
  // Ícones do alternador de visualização (lista x kanban)
  List,
  LayoutGrid,
} from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { useContractActions } from '@/hooks/useContractActions';
import type { ContractStatus } from '@/types/contracts';
import { SIGNER_ROLES } from '@/types/contracts';
import { usePublishedContracts } from '@/hooks/usePublishedContracts';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { useAuth } from '@/hooks/useAuth';
import { CrmPeriodFilter } from '@/components/crm/filters/CrmPeriodFilter';
import { CrmUserFilter } from '@/components/crm/filters/CrmUserFilter';
import type { PeriodFilter, PeriodPreset } from '@/hooks/useCrmOverviewFilters';
import { startOfDay, endOfDay, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { useContractsPaginated, ContractFilters, ContractSortField, SortDirection, PaginatedContract } from '@/hooks/useContractsPaginated';
import { useContractListKpis } from '@/hooks/useContractListKpis';
import { ContractListKpiCards } from '@/components/contracts/ContractListKpiCards';
import { useCrmSellers } from '@/hooks/useCrmSellers';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { ContractWizardDialog } from '@/components/contracts/wizard/ContractWizardDialog';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { MonthNavigator } from '@/components/financial/MonthNavigator';
import { CONTRACT_STATUS_LABELS } from '@/types/contracts';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// =============================================================================
// ContractRowActions foi extraído para @/components/contracts/ContractRowActions
// para ser reaproveitado também pelos cards do Kanban de Contratos.
// =============================================================================
import { ContractRowActions } from '@/components/contracts/ContractRowActions';
// Board do Kanban de Contratos (visualização alternativa por status)
import { ContractKanbanBoard } from '@/components/contracts/kanban/ContractKanbanBoard';




/**
 * Mapeamento de cor da barra lateral por status do contrato
 * Segue o mesmo padrão visual do DealPriorityIndicator
 */
function getContractStatusBarColor(status: string): string {
  switch (status) {
    case 'draft':
      return 'bg-muted-foreground/40';
    case 'generated':
    case 'queued_pdf':
    case 'processing_pdf':
      return 'bg-blue-500';
    case 'sent':
    case 'queued_signature':
    case 'processing_signature':
      return 'bg-amber-500';
    case 'signed':
      return 'bg-green-500';
    case 'expired':
    case 'error':
      return 'bg-red-500';
    case 'cancelled':
      return 'bg-destructive';
    default:
      return 'bg-muted-foreground/40';
  }
}

/**
 * Componente auxiliar para renderizar cabeçalho de coluna ordenável
 */
function SortableHeader({
  label,
  field,
  currentField,
  currentDirection,
  onSort,
}: {
  label: string;
  field: ContractSortField;
  currentField: ContractSortField;
  currentDirection: SortDirection;
  onSort: (field: ContractSortField) => void;
}) {
  return (
    <TableHead
      className="text-xs font-medium cursor-pointer hover:bg-muted/30 transition-colors select-none"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {currentField === field ? (
          currentDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );
}

/**
 * Página de listagem de Contratos Gerados com paginação server-side
 */
export default function CrmGeneratedContracts() {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();

  // Hook para refresh com timestamp
  const { isRefreshing, formattedTime, triggerRefresh } = useRefreshWithTimestamp();

  // Estados de filtro
  const [search, setSearch] = useState('');
  // v1.5.0: controla a busca expansível (sanfona) do modo Kanban
  // v1.6.0: busca começa recolhida; expande somente ao clicar na lupa
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [workspaceFilter, setWorkspaceFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sellerFilter, setSellerFilter] = useState<string>('all');
  // v1.5.0: padrão passa a ser "Atualizado em" (alinha a listagem ao Kanban)
  const [dateFieldFilter, setDateFieldFilter] = useState<string>('updated_at');
  // Filtros avançados (botão "Mais")
  const [signerRoleFilter, setSignerRoleFilter] = useState<string>('all');
  const [publishedContractFilter, setPublishedContractFilter] = useState<string>('all');
  const [pendingSignerRoleFilter, setPendingSignerRoleFilter] = useState<string>('all');

  // Estado do MonthNavigator (navegação rápida por mês de criação)
  const [monthFilter, setMonthFilter] = useState<Date | null>(new Date());

  // Estado do filtro de período (preset + datas)
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>({
    preset: 'month',
    startDate: startOfMonth(new Date()),
    endDate: endOfDay(new Date()),
  });

  // v1.7.0 — Filtro rápido de data EXCLUSIVO do Kanban.
  // Estados separados dos da Lista para não interferir no padrão "Atualizado em"
  // da listagem. Por padrão o Kanban abre sem recorte de data ("Todas datas").
  const [kanbanDateField, setKanbanDateField] = useState<string>('all');
  const [kanbanPeriod, setKanbanPeriod] = useState<PeriodFilter>({
    preset: 'month',
    startDate: startOfMonth(new Date()),
    endDate: endOfDay(new Date()),
  });

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Estados de ordenação
  // v1.5.0: ordenação padrão por data de alteração (contratos que mudaram de status primeiro)
  const [sortField, setSortField] = useState<ContractSortField>('updated_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Estado do modal de detalhes do deal
  const [viewDealId, setViewDealId] = useState<string | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Estado do wizard standalone de novo contrato
  const [wizardOpen, setWizardOpen] = useState(false);

  // Modo de visualização (lista x kanban)
  // Regra v1.3.0: a URL é a ÚNICA fonte de verdade. Sem `?view=kanban` a tela SEMPRE abre
  // em listagem — o kanban tem menu próprio ("Kanban de Contratos"), portanto não há mais
  // persistência de preferência em localStorage.
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');

  const [viewMode, setViewMode] = useState<'list' | 'kanban'>(
    viewParam === 'kanban' ? 'kanban' : 'list',
  );

  // Reage a mudanças posteriores da URL (ex.: clique no menu enquanto já está na página)
  useEffect(() => {
    setViewMode(viewParam === 'kanban' ? 'kanban' : 'list');
  }, [viewParam]);


  /**
   * Alterna o modo de visualização mantendo a URL sincronizada (link compartilhável).
   * O modo "list" remove o parâmetro para que a rota limpa signifique sempre listagem.
   */
  const handleViewModeChange = useCallback((mode: 'list' | 'kanban') => {
    setViewMode(mode);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (mode === 'kanban') next.set('view', 'kanban');
      else next.delete('view');
      return next;
    }, { replace: true });
  }, [setSearchParams]);




  // Quick Create (?new=1): abre o wizard "Novo contrato".
  useAutoOpenFromQuery('1', () => setWizardOpen(true));


  // Busca hierarquia de workspaces
  const { workspaces } = useCrmWorkspacesHierarchy();

  // Busca vendedores ativos para filtro
  const { activeSellers } = useCrmSellers();

  // Busca contratos publicados (templates) para filtro avançado
  const { publishedContracts } = usePublishedContracts();

  // Contagem de filtros extras ativos (botão "Mais")
  const extraFiltersCount = [signerRoleFilter !== 'all', publishedContractFilter !== 'all', pendingSignerRoleFilter !== 'all'].filter(Boolean).length;

  // v2.0.0: Estado do Sheet lateral de filtros avançados
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  // Estado do KPI ativo (filtro via cards)
  const [activeKpiStatus, setActiveKpiStatus] = useState<string | null>(null);

  /**
   * Handler para filtrar por KPI card (toggle)
   * Aplica filtro de data para "created_today" e status para os demais
   */
  const handleKpiFilter = (status: string | null) => {
    // Toggle: se já está ativo, limpa
    if (activeKpiStatus === status) {
      setActiveKpiStatus(null);
      setStatusFilter('all');
      setDateFieldFilter('all');
      return;
    }

    setActiveKpiStatus(status);

    if (status === null) {
      setStatusFilter('all');
      setDateFieldFilter('all');
    } else if (status === 'created_today') {
      // "Criados Hoje": limpa status e aplica filtro de data criação = hoje
      setStatusFilter('all');
      setDateFieldFilter('created_at');
      const now = new Date();
      setPeriodFilter({
        preset: 'today',
        startDate: startOfDay(now),
        endDate: endOfDay(now),
      });
    } else if (status === 'pending_signature') {
      setStatusFilter('sent');
      setDateFieldFilter('all');
    } else {
      setStatusFilter(status);
      setDateFieldFilter('all');
    }
  };

  // Reset de página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [search, workspaceFilter, statusFilter, sellerFilter, dateFieldFilter, periodFilter, monthFilter, signerRoleFilter, publishedContractFilter, pendingSignerRoleFilter]);

  // Limpa período ao selecionar "Todas datas"
  useEffect(() => {
    if (dateFieldFilter === 'all') {
      setPeriodFilter({
        preset: 'month',
        startDate: startOfMonth(new Date()),
        endDate: endOfDay(new Date()),
      });
    }
  }, [dateFieldFilter]);

  // Handlers para CrmPeriodFilter
  const handlePeriodPresetChange = (preset: PeriodPreset) => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = endOfDay(now);
    switch (preset) {
      case 'today': startDate = startOfDay(now); break;
      case 'yesterday': startDate = startOfDay(subDays(now, 1)); endDate = endOfDay(subDays(now, 1)); break;
      case '7days': startDate = startOfDay(subDays(now, 6)); break;
      case '15days': startDate = startOfDay(subDays(now, 14)); break;
      case 'month': startDate = startOfMonth(now); break;
      case 'lastMonth': startDate = startOfMonth(subMonths(now, 1)); endDate = endOfMonth(subMonths(now, 1)); break;
      default: startDate = startOfMonth(now);
    }
    setPeriodFilter({ preset, startDate, endDate });
  };

  const handlePeriodCustomChange = (startDate: Date, endDate: Date) => {
    setPeriodFilter({ preset: 'custom', startDate: startOfDay(startDate), endDate: endOfDay(endDate) });
  };

  /**
   * v1.7.0 — Calcula o intervalo de um preset de período (reaproveitado pelo Kanban).
   */
  const resolvePresetRange = useCallback((preset: PeriodPreset): { startDate: Date; endDate: Date } => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = endOfDay(now);
    switch (preset) {
      case 'today': startDate = startOfDay(now); break;
      case 'yesterday': startDate = startOfDay(subDays(now, 1)); endDate = endOfDay(subDays(now, 1)); break;
      case '7days': startDate = startOfDay(subDays(now, 6)); break;
      case '15days': startDate = startOfDay(subDays(now, 14)); break;
      case 'month': startDate = startOfMonth(now); break;
      case 'lastMonth': startDate = startOfMonth(subMonths(now, 1)); endDate = endOfMonth(subMonths(now, 1)); break;
      default: startDate = startOfMonth(now);
    }
    return { startDate, endDate };
  }, []);

  // Handlers do filtro de período do Kanban
  const handleKanbanPresetChange = useCallback((preset: PeriodPreset) => {
    const { startDate, endDate } = resolvePresetRange(preset);
    setKanbanPeriod({ preset, startDate, endDate });
  }, [resolvePresetRange]);

  const handleKanbanCustomChange = useCallback((startDate: Date, endDate: Date) => {
    setKanbanPeriod({ preset: 'custom', startDate: startOfDay(startDate), endDate: endOfDay(endDate) });
  }, []);

  // Handler para ordenação por colunas
  const handleSort = (field: ContractSortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  // Handler para mudança de mês no MonthNavigator
  const handleMonthChange = useCallback((newDate: Date) => {
    setMonthFilter(newDate);
  }, []);

  // Handler para limpar filtro de mês (ver todos)
  const handleMonthClear = useCallback(() => {
    setMonthFilter(null);
  }, []);

  // Calcula intervalo de datas baseado no MonthNavigator ou filtros manuais
  // v1.5.0: o MonthNavigator aplica o mês sobre o campo de data escolhido pelo usuário
  // (padrão "Atualizado em"); só cai em created_at quando o usuário marca "Todas datas".
  const resolvedDateField = monthFilter
    ? (dateFieldFilter !== 'all' ? dateFieldFilter : 'created_at')
    : (dateFieldFilter !== 'all' ? dateFieldFilter : undefined);
  const resolvedDateFrom = monthFilter
    ? startOfMonth(monthFilter).toISOString()
    : (dateFieldFilter !== 'all' ? periodFilter.startDate.toISOString() : undefined);
  const resolvedDateTo = monthFilter
    ? endOfMonth(monthFilter).toISOString()
    : (dateFieldFilter !== 'all' ? periodFilter.endDate.toISOString() : undefined);

  // Monta filtros para o hook da listagem
  const filters: ContractFilters = useMemo(() => ({
    workspace_id: workspaceFilter !== 'all' ? workspaceFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    search: search || undefined,
    seller_id: sellerFilter !== 'all' ? sellerFilter : undefined,
    date_field: resolvedDateField,
    date_from: resolvedDateFrom,
    date_to: resolvedDateTo,
    signer_role: signerRoleFilter !== 'all' ? signerRoleFilter : undefined,
    published_contract_id: publishedContractFilter !== 'all' ? publishedContractFilter : undefined,
    pending_signer_role: pendingSignerRoleFilter !== 'all' ? pendingSignerRoleFilter : undefined,
  }), [workspaceFilter, statusFilter, search, sellerFilter, resolvedDateField, resolvedDateFrom, resolvedDateTo, signerRoleFilter, publishedContractFilter, pendingSignerRoleFilter]);

  /**
   * Filtros do Kanban: mesmos filtros da listagem, porém com recorte de data próprio.
   * v1.7.0: o Kanban ganhou filtro rápido de data (campo + período), desligado por padrão
   * ("Todas datas") para preservar o comportamento de listar o que mudou por último.
   */
  const kanbanFilters: ContractFilters = useMemo(() => ({
    workspace_id: workspaceFilter !== 'all' ? workspaceFilter : undefined,
    search: search || undefined,
    seller_id: sellerFilter !== 'all' ? sellerFilter : undefined,
    date_field: kanbanDateField !== 'all' ? kanbanDateField : undefined,
    date_from: kanbanDateField !== 'all' ? kanbanPeriod.startDate.toISOString() : undefined,
    date_to: kanbanDateField !== 'all' ? kanbanPeriod.endDate.toISOString() : undefined,
    signer_role: signerRoleFilter !== 'all' ? signerRoleFilter : undefined,
    published_contract_id: publishedContractFilter !== 'all' ? publishedContractFilter : undefined,
    pending_signer_role: pendingSignerRoleFilter !== 'all' ? pendingSignerRoleFilter : undefined,
  }), [workspaceFilter, search, sellerFilter, kanbanDateField, kanbanPeriod, signerRoleFilter, publishedContractFilter, pendingSignerRoleFilter]);



  // v1.2.0: Filtros de KPI separados para evitar circularidade — exclui filtros aplicados pelo card KPI
  const kpiFilters: ContractFilters = useMemo(() => ({
    workspace_id: workspaceFilter !== 'all' ? workspaceFilter : undefined,
    status: (activeKpiStatus && activeKpiStatus !== 'created_today') ? undefined : (statusFilter !== 'all' ? statusFilter : undefined),
    search: search || undefined,
    seller_id: sellerFilter !== 'all' ? sellerFilter : undefined,
    date_field: activeKpiStatus === 'created_today' ? undefined : resolvedDateField,
    date_from: activeKpiStatus === 'created_today' ? undefined : resolvedDateFrom,
    date_to: activeKpiStatus === 'created_today' ? undefined : resolvedDateTo,
    signer_role: signerRoleFilter !== 'all' ? signerRoleFilter : undefined,
    published_contract_id: publishedContractFilter !== 'all' ? publishedContractFilter : undefined,
    pending_signer_role: pendingSignerRoleFilter !== 'all' ? pendingSignerRoleFilter : undefined,
  }), [workspaceFilter, statusFilter, search, sellerFilter, resolvedDateField, resolvedDateFrom, resolvedDateTo, activeKpiStatus, signerRoleFilter, publishedContractFilter, pendingSignerRoleFilter]);

  // Busca KPIs totalizadores dos contratos (usa filtros sem circularidade)
  const { kpis: contractKpis, isLoading: kpisLoading } = useContractListKpis(kpiFilters);

  // Busca contratos com paginação server-side
  const {
    contracts,
    totalCount,
    isLoading,
    isFetching,
    refetch,
  } = useContractsPaginated(filters, {
    page: currentPage,
    pageSize,
    sortField,
    sortDirection,
  });

  // Verifica se há filtros ativos (exclui monthFilter pois é o padrão)
  // v1.5.0: 'updated_at' é o padrão da página, então não conta como filtro ativo
  // v1.7.0: no Kanban o recorte de data próprio ('all' por padrão) também conta como filtro ativo
  const hasActiveFilters = search || workspaceFilter !== 'all' || statusFilter !== 'all' || sellerFilter !== 'all'
    || (viewMode === 'kanban' ? kanbanDateField !== 'all' : dateFieldFilter !== 'updated_at')
    || signerRoleFilter !== 'all' || publishedContractFilter !== 'all' || pendingSignerRoleFilter !== 'all';

  // Limpa todos os filtros
  const clearFilters = () => {
    setSearch('');
    setWorkspaceFilter('all');
    setStatusFilter('all');
    setSellerFilter('all');
    // v1.5.0: limpar volta ao padrão da página ("Atualizado em"), não a "Todas datas"
    setDateFieldFilter('updated_at');
    setActiveKpiStatus(null);
    setSignerRoleFilter('all');
    setPublishedContractFilter('all');
    setPendingSignerRoleFilter('all');
    setMonthFilter(new Date());
    setPeriodFilter({
      preset: 'month',
      startDate: startOfMonth(new Date()),
      endDate: endOfDay(new Date()),
    });
    // v1.7.0: limpa também o recorte de data próprio do Kanban
    setKanbanDateField('all');
    setKanbanPeriod({
      preset: 'month',
      startDate: startOfMonth(new Date()),
      endDate: endOfDay(new Date()),
    });
  };

  // Função para atualizar dados manualmente
  const handleRefresh = async () => {
    await triggerRefresh(async () => {
      await refetch();
    });
  };

  // Abre modal de detalhes do deal correspondente
  const handleViewDeal = (contract: PaginatedContract) => {
    if (contract.deal_id) {
      setViewDealId(contract.deal_id);
      setViewModalOpen(true);
    }
  };

  // Formata número do contrato (8 dígitos)
  const formatContractNumber = (num: number | null) => {
    if (!num) return '-';
    return String(num).padStart(8, '0');
  };

  // Status badges filtráveis (somente status principais)
  const mainStatuses = ['draft', 'generated', 'sent', 'signed', 'expired', 'cancelled', 'error'] as const;

  /**
   * v1.6.0 — Filtros rápidos compartilhados entre Lista e Kanban.
   * `compact` aplica o visual de pílula do Kanban de Negócios (altura 22px);
   * quando falso, mantém exatamente o tamanho atual da barra da listagem.
   * O comportamento (estados, Sheet avançado, dados) é idêntico nos dois modos.
   */
  const renderQuickFilters = (compact: boolean) => {
    // Classes de gatilho dos selects conforme o modo
    const triggerBase = compact
      ? 'h-[22px] px-2 text-xs gap-1.5 w-auto'
      : 'h-6 text-[11px] sm:h-8 sm:text-sm';
    const iconClass = compact ? 'h-3 w-3 shrink-0 text-muted-foreground' : 'h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground';

    return (
      <>
        {/* Workspace */}
        <Select value={workspaceFilter} onValueChange={setWorkspaceFilter}>
          <SelectTrigger
            className={cn(
              triggerBase,
              compact
                ? cn('max-w-[150px] shrink-0', workspaceFilter !== 'all' && 'border-primary bg-primary/5')
                : 'w-[100px] sm:w-[160px]',
            )}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <Building2 className={iconClass} />
              <span className="truncate">
                {workspaceFilter === 'all' ? 'Workspace' : workspaces.find(w => w.id === workspaceFilter)?.name || 'Workspace'}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos workspaces</SelectItem>
            {workspaces.map(w => (
              <SelectItem key={w.id} value={w.id}>
                <div className="flex items-center gap-2">
                  {w.color && (
                    <span
                      className="h-2 w-2 rounded-full shrink-0"
                      style={{ backgroundColor: w.color }}
                    />
                  )}
                  {w.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status Contrato */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger
            className={cn(
              triggerBase,
              compact
                ? cn('max-w-[140px] shrink-0', statusFilter !== 'all' && 'border-primary bg-primary/5')
                : 'w-[95px] sm:w-[150px]',
            )}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <PlayCircle className={iconClass} />
              <span className="truncate">
                {statusFilter === 'all' ? 'Status' : CONTRACT_STATUS_LABELS[statusFilter]?.label || statusFilter}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            {mainStatuses.map(s => (
              <SelectItem key={s} value={s}>
                {CONTRACT_STATUS_LABELS[s]?.label || s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Vendedor (padrão CrmUserFilter com busca e avatar) */}
        <CrmUserFilter
          value={sellerFilter === 'all' ? null : sellerFilter}
          onChange={(id) => setSellerFilter(id || 'all')}
          label="Vendedor"
          searchPlaceholder="Buscar vendedor..."
          allLabel="Todos Vendedores"
          groupLabel="Vendedores"
          className={compact ? 'h-[22px] px-2 text-xs shrink-0' : 'h-6 text-[11px] sm:h-8 sm:text-sm'}
        />

        {/* v1.7.0 — Filtro rápido de data (somente Kanban; na Lista ele vive na barra de filtros) */}
        {compact && (
          <>
            <Select value={kanbanDateField} onValueChange={setKanbanDateField}>
              <SelectTrigger
                className={cn(
                  triggerBase,
                  'max-w-[150px] shrink-0',
                  kanbanDateField !== 'all' && 'border-primary bg-primary/5',
                )}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <Calendar className={iconClass} />
                  <span className="truncate">
                    {kanbanDateField === 'all'
                      ? 'Período'
                      : ({ created_at: 'Criado em', updated_at: 'Atualizado em', sent_at: 'Enviado em', signed_at: 'Assinado em' } as Record<string, string>)[kanbanDateField] || 'Período'}
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas datas</SelectItem>
                <SelectItem value="created_at">Criado em</SelectItem>
                <SelectItem value="sent_at">Enviado em</SelectItem>
                <SelectItem value="signed_at">Assinado em</SelectItem>
                <SelectItem value="updated_at">Atualizado em</SelectItem>
              </SelectContent>
            </Select>

            {/* Seletor de período — aparece apenas quando há campo de data escolhido */}
            {kanbanDateField !== 'all' && (
              <CrmPeriodFilter
                value={kanbanPeriod}
                onPresetChange={handleKanbanPresetChange}
                onCustomChange={handleKanbanCustomChange}
                className="h-[22px] px-2 text-xs shrink-0"
              />
            )}
          </>
        )}



        {/* Botão "Mais" — abre o Sheet de filtros avançados */}
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'gap-1.5 shrink-0',
            compact ? 'h-[22px] px-2 text-xs' : 'h-6 px-2 text-[10px] sm:h-8 sm:px-3 sm:text-xs',
          )}
          onClick={() => setIsMoreFiltersOpen(true)}
        >
          <SlidersHorizontal className={compact ? 'h-3 w-3' : 'h-3 w-3 sm:h-3.5 sm:w-3.5'} />
          Mais
          {extraFiltersCount > 0 && (
            <Badge variant="secondary" className="h-4 min-w-[16px] px-1 text-[10px] rounded-full bg-primary/15 text-primary border-0">
              {extraFiltersCount}
            </Badge>
          )}
        </Button>

        {/* Limpar filtros */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className={cn(
              'text-muted-foreground hover:text-foreground shrink-0',
              compact ? 'h-[22px] px-2 text-xs' : 'h-8 px-2 text-xs',
            )}
          >
            <X className="h-3 w-3 mr-1" />
            Limpar
          </Button>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto Premium */}
      {/* Mobile: linha 1 = título + ações | linha 2 = navegador de mês centralizado. */}
      <div className="flex flex-col gap-3 p-4 border-b sm:flex-row sm:items-center sm:justify-between">
        {/* Título — abriga ações no mobile alinhadas à direita */}
        <div className="flex items-center justify-between gap-2 sm:shrink-0">
          <div className="flex items-center gap-1">
            <div>
              <h1 className="text-xl font-semibold">Contratos Gerados</h1>
            </div>

            {/* v1.5.0: Busca expansível (sanfona) — mesmo padrão do Kanban de Negócios */}
            {viewMode === 'kanban' && (
              <div className="hidden md:flex items-center">
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-300 ease-in-out',
                    isSearchExpanded ? 'w-44 opacity-100 ml-2 mr-1' : 'w-0 opacity-0',
                  )}
                >
                  <div className="relative w-full">
                    <Input
                      placeholder="Buscar..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onBlur={() => { if (!search) setIsSearchExpanded(false); }}
                      className="h-7 w-full text-xs rounded-full pl-3 pr-7"
                      autoFocus={isSearchExpanded}
                    />
                    {search && (
                      <button
                        onClick={() => setSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-full shrink-0"
                  onClick={() => setIsSearchExpanded((prev) => (prev && !search ? false : !prev))}
                  title="Buscar contratos"
                >
                  {isSearchExpanded ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            )}
          </div>
          {/* Ações mobile */}
          <div className="flex items-center gap-2 sm:hidden">
            <Button
              variant="outline"
              size="icon-sm"
              onClick={handleRefresh}
              disabled={isRefreshing || isFetching}
              title="Atualizar dados"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", (isRefreshing || isFetching) && "animate-spin")} />
            </Button>
            <Button
              size="xs"
              onClick={() => setWizardOpen(true)}
              className="gap-1.5 hover:shadow-md hover:scale-[1.02] transition-all duration-200"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        {/* v1.6.0: no Kanban os filtros rápidos ficam na linha do título (padrão Negócios).
            No mobile a linha rola horizontalmente e mantém uma busca compacta. */}
        {viewMode === 'kanban' && (
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide min-w-0 sm:flex-1">
            <Separator orientation="vertical" className="h-5 hidden sm:block shrink-0" />

            {/* Busca compacta apenas no mobile (no desktop existe a lupa expansível) */}
            <div className="relative md:hidden shrink-0 w-40">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-7 h-[22px] text-xs rounded-full"
              />
            </div>

            {renderQuickFilters(true)}
          </div>
        )}




        {/* Navegador de mês — centralizado no mobile (oculto no Kanban: lá o corte é por data de alteração) */}
        {monthFilter && viewMode === 'list' && (

          <div className="flex justify-center sm:contents">
            <MonthNavigator
              currentMonth={monthFilter}
              onChange={handleMonthChange}
              onClear={handleMonthClear}
            />
          </div>
        )}
        {/* Ações desktop à direita */}
        <div className="hidden sm:flex items-center gap-2">
          {/* Alternador Lista / Kanban — mesma fonte de filtros nos dois modos */}
          <div className="flex items-center rounded-md border p-0.5">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon-sm"
              onClick={() => handleViewModeChange('list')}
              title="Visualizar em lista"
            >
              <List className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
              size="icon-sm"
              onClick={() => handleViewModeChange('kanban')}
              title="Visualizar em kanban por status"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </Button>
          </div>

          <Button
            variant="outline"
            size="icon-sm"
            onClick={handleRefresh}
            disabled={isRefreshing || isFetching}
            title="Atualizar dados"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", (isRefreshing || isFetching) && "animate-spin")} />
          </Button>
          <Button
            size="xs"
            onClick={() => setWizardOpen(true)}
            className="gap-1.5 hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <Plus className="mr-1.5" />
            Novo Contrato
          </Button>
        </div>
      </div>

      {/* Cards de KPI totalizadores — ocultos no modo Kanban para ganhar espaço vertical */}
      {viewMode === 'list' && (
      <ContractListKpiCards
        kpis={contractKpis}
        loading={kpisLoading}
        activeStatus={activeKpiStatus ?? undefined}
        onFilterByStatus={handleKpiFilter}
      />
      )}

      {/* Barra de Filtros Premium — v1.6.0: exclusiva do modo Lista.
          No Kanban os mesmos filtros vivem na linha do título (padrão Negócios). */}
      {viewMode === 'list' && (
      <div className="flex flex-wrap items-center gap-1 p-2 sm:gap-2 sm:p-3 border-b bg-muted/30">
        {/* Busca */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por cliente, negócio, telefone, CPF..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-6 text-[11px] sm:h-8 sm:text-sm"
          />
        </div>

        {/* Campo de Data — apenas no modo Lista (o Kanban usa data de alteração, sem recorte por período) */}
        <Select value={dateFieldFilter} onValueChange={setDateFieldFilter}>
          <SelectTrigger className="w-[95px] h-6 text-[11px] sm:w-[140px] sm:h-8 sm:text-sm border-0 bg-background rounded-full">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {dateFieldFilter === 'all' ? 'Período' : { created_at: 'Criado em', updated_at: 'Atualizado em', sent_at: 'Enviado em', signed_at: 'Assinado em' }[dateFieldFilter] || 'Período'}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas datas</SelectItem>
            <SelectItem value="created_at">Criado em</SelectItem>
            <SelectItem value="sent_at">Enviado em</SelectItem>
            <SelectItem value="signed_at">Assinado em</SelectItem>
            {/* v1.5.0: recorte por data de alteração (padrão da página) */}
            <SelectItem value="updated_at">Atualizado em</SelectItem>
          </SelectContent>
        </Select>

        {/* Seletor de período - aparece quando campo de data está selecionado */}
        {dateFieldFilter !== 'all' && (
          <CrmPeriodFilter
            value={periodFilter}
            onPresetChange={handlePeriodPresetChange}
            onCustomChange={handlePeriodCustomChange}
            className="h-6 text-[11px] sm:h-8 sm:text-sm"
          />
        )}

        {/* Filtros rápidos compartilhados (Workspace / Status / Vendedor / Mais / Limpar) */}
        {renderQuickFilters(false)}

        {/* Indicador de carregamento */}
        {isFetching && !isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            Atualizando...
          </span>
        )}
      </div>
      )}

      {/* Sheet de filtros avançados — único, compartilhado por Lista e Kanban */}
      <Sheet open={isMoreFiltersOpen} onOpenChange={setIsMoreFiltersOpen}>
        <SheetContent side="right" className="w-80 p-0 flex flex-col">
          <SheetHeader className="p-4 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-sm">Filtros Avançados</SheetTitle>
              {extraFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground"
                  onClick={() => {
                    setSignerRoleFilter('all');
                    setPublishedContractFilter('all');
                    setPendingSignerRoleFilter('all');
                  }}
                >
                  <X className="h-3 w-3 mr-1" />
                  Limpar
                </Button>
              )}
            </div>
          </SheetHeader>
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {/* Filtro por Template (Contrato Publicado) */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <FileText className="h-3 w-3" />
                  Template
                </Label>
                <Select value={publishedContractFilter} onValueChange={setPublishedContractFilter}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Todos templates" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos templates</SelectItem>
                    {(publishedContracts || []).map((pc: any) => (
                      <SelectItem key={pc.id} value={pc.id}>
                        {pc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Tipo Signatário */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <UserCheck className="h-3 w-3" />
                  Tipo Signatário
                </Label>
                <Select value={signerRoleFilter} onValueChange={setSignerRoleFilter}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Todos tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos tipos</SelectItem>
                    {SIGNER_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Pendente Assinatura */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <FileCheck className="h-3 w-3" />
                  Pendente Assinatura
                </Label>
                <Select value={pendingSignerRoleFilter} onValueChange={setPendingSignerRoleFilter}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Todos tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos tipos</SelectItem>
                    {SIGNER_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>


      {/* Kanban por status — colunas fixas, sem arrastar e soltar */}
      {viewMode === 'kanban' && currentTenant?.id && (
        /* Wrapper com altura contida: evita que o board empurre/role a página */
        <div className="flex-1 min-h-0 overflow-hidden">
          <ContractKanbanBoard
            tenantId={currentTenant.id}
            filters={kanbanFilters}
            onViewDeal={(dealId) => {
              setViewDealId(dealId);
              setViewModalOpen(true);
            }}
          />
        </div>
      )}

      {/* Tabela Premium — com setinhas de scroll horizontal */}
      {viewMode === 'list' && (
      <ScrollIndicators className="flex-1">
        <div className="h-full w-full overflow-hidden">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : contracts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <FileSignature className="h-12 w-12 text-muted-foreground/40" />
            <div className="text-center">
              <h3 className="font-medium text-sm">Nenhum contrato encontrado</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {hasActiveFilters
                  ? 'Tente ajustar os filtros de busca'
                  : 'Nenhum contrato foi gerado ainda'}
              </p>
            </div>
          </div>
        ) : (
          <Table scrollViewport containerClassName="h-full" className="min-w-[1700px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {/* Barra colorida de status */}
                <TableHead className="w-2 p-0" />
                {/* Número - ordenável */}
                <SortableHeader label="Nº" field="contract_number" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                {/* Título/Negócio - não ordenável */}
                <TableHead className="text-xs font-medium">Contrato / Negócio</TableHead>
                {/* Cliente - ordenável */}
                <SortableHeader label="Cliente" field="contact_name" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                {/* Valor - ordenável */}
                <SortableHeader label="Valor" field="deal_value" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                {/* Status - ordenável */}
                <SortableHeader label="Status" field="status" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                {/* Assinaturas - não ordenável */}
                <TableHead className="text-xs font-medium">Assinaturas</TableHead>
                {/* Provider - não ordenável */}
                <TableHead className="text-xs font-medium">Provider</TableHead>
                {/* Template - não ordenável */}
                <TableHead className="text-xs font-medium">Template</TableHead>
                {/* Criado em - ordenável */}
                <SortableHeader label="Criado" field="created_at" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                {/* v1.5.0: Atualizado em - ordenável (padrão da listagem) */}
                <SortableHeader label="Atualizado" field="updated_at" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                {/* Enviado em - ordenável */}
                <SortableHeader label="Enviado" field="sent_at" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                {/* Assinado em - ordenável */}
                <SortableHeader label="Assinado" field="signed_at" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                {/* Vendedor - ordenável */}
                <SortableHeader label="Vendedor" field="seller_name" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                {/* Responsável - ordenável */}
                <SortableHeader label="Responsável" field="assigned_user_name" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                {/* Ações */}
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {contracts.map(contract => {
                const statusInfo = CONTRACT_STATUS_LABELS[contract.status as ContractStatus];
                const signProgress = contract.signers_count > 0
                  ? Math.round((contract.signed_count / contract.signers_count) * 100)
                  : 0;

                return (
                  <TableRow
                    key={contract.id}
                    className="cursor-pointer hover:bg-muted/40 group"
                    onClick={() => handleViewDeal(contract)}
                  >
                    {/* Barra lateral colorida baseada no status */}
                    <TableCell className="p-0 w-2">
                      <div className={cn(
                        'w-1 h-full min-h-[40px] rounded-full',
                        getContractStatusBarColor(contract.status)
                      )} />
                    </TableCell>
                    {/* Número do contrato */}
                    <TableCell className="py-2">
                      <span className="text-xs font-mono text-muted-foreground">
                        {formatContractNumber(contract.contract_number)}
                      </span>
                    </TableCell>

                    {/* Título + Negócio */}
                    <TableCell className="py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate max-w-[180px]">{contract.title}</p>
                        {contract.deal_title && (
                          <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {contract.deal_title}
                          </p>
                        )}
                      </div>
                    </TableCell>

                    {/* Cliente — padrão unificado: avatar (iniciais) + nome truncado */}
                    <TableCell className="py-2">
                      {contract.contact_name ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <SmartAvatar
                            src={contract.contact_avatar}
                            name={contract.contact_name}
                            size="xs"
                          />
                          <div className="min-w-0">
                            <p className="text-sm truncate max-w-[120px]">{contract.contact_name}</p>
                            {contract.contact_phone && (
                              <p className="text-xs text-muted-foreground truncate max-w-[120px]">{contract.contact_phone}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Valor do Deal */}
                    <TableCell className="py-2">
                      {contract.deal_value ? (
                        <span className="text-sm font-mono font-medium">
                          <PriceDisplay amount={contract.deal_value} currency={contract.deal_currency || 'BRL'} size="sm" />
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Status — bolinha colorida + rótulo em cor padrão (substitui o badge) */}
                    <TableCell className="py-2">
                      {statusInfo ? (
                        <div className="flex items-center gap-1.5">
                          <span
                            className={cn(
                              'h-2 w-2 shrink-0 rounded-full',
                              getContractStatusBarColor(contract.status)
                            )}
                          />
                          <span className="text-xs text-foreground whitespace-nowrap">{statusInfo.label}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">{contract.status}</span>
                      )}
                    </TableCell>


                    {/* Assinaturas */}
                    <TableCell className="py-2">
                      {contract.signers_count > 0 ? (
                        <div className="flex items-center gap-2 min-w-[80px]">
                          <Progress value={signProgress} className="h-1.5 w-12" />
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {contract.signed_count}/{contract.signers_count}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Provider */}
                    <TableCell className="py-2">
                      {contract.provider_display_name ? (
                        <span className="text-xs text-muted-foreground">{contract.provider_display_name}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Template + Versão */}
                    <TableCell className="py-2">
                      {contract.template_name ? (
                        <div className="min-w-0">
                          <p className="text-xs truncate max-w-[120px]">{contract.template_name}</p>
                          {contract.version_number && (
                            <p className="text-[10px] text-muted-foreground">v1.{contract.version_number - 1}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Criado em */}
                    <TableCell className="py-2">
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(contract.created_at), 'dd/MM/yy', { locale: ptBR })}
                      </span>
                    </TableCell>

                    {/* v1.5.0: Atualizado em */}
                    <TableCell className="py-2">
                      {contract.updated_at ? (
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(contract.updated_at), 'dd/MM/yy', { locale: ptBR })}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Enviado em */}
                    <TableCell className="py-2">
                      {contract.sent_at ? (
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(contract.sent_at), 'dd/MM/yy', { locale: ptBR })}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Assinado em */}
                    <TableCell className="py-2">
                      {contract.signed_at ? (
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(contract.signed_at), 'dd/MM/yy', { locale: ptBR })}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Vendedor */}
                    <TableCell className="py-2">
                      {contract.seller_id ? (
                        <div className="flex items-center gap-2">
                          <SmartAvatar
                            src={contract.seller_avatar}
                            name={contract.seller_name}
                            size="xs"
                            use3DFallback
                          />
                          <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                            {contract.seller_name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Responsável */}
                    <TableCell className="py-2">
                      {contract.assigned_to ? (
                        <div className="flex items-center gap-2">
                          <SmartAvatar
                            src={contract.assigned_user_avatar}
                            name={contract.assigned_user_name}
                            size="xs"
                            use3DFallback
                          />
                          <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                            {contract.assigned_user_name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Ações contextuais */}
                    <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                      <ContractRowActions
                        contract={contract}
                        tenantId={currentTenant?.id || ''}
                        onViewDeal={() => handleViewDeal(contract)}
                        onActionComplete={() => {
                          queryClient.invalidateQueries({ queryKey: ['contracts-paginated'] });
                          queryClient.invalidateQueries({ queryKey: ['contract-list-kpis'] });
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        </div>
      </ScrollIndicators>
      )}

      {/* Footer de Paginação — apenas no modo lista (kanban usa infinite scroll por coluna) */}
      {viewMode === 'list' && !isLoading && contracts.length > 0 && (

        <div className="border-t px-4 py-2 bg-background">
          <PaginationFooter
            totalItems={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
            pageSizeOptions={[10, 20, 50, 100]}
            borderClassName=""
            className="mt-0 pt-0"
          />
        </div>
      )}

      {/* Modal Premium de Detalhes do Deal */}
      <DealDetailsModalPremium
        dealId={viewDealId}
        open={viewModalOpen}
        onOpenChange={setViewModalOpen}
        onDeleted={() => {
          queryClient.invalidateQueries({ queryKey: ['contracts-paginated'] });
        }}
        onOpenConversation={(conversationId) => {
          console.log('Abrir conversa:', conversationId);
        }}
      />

      {/* Wizard standalone de geração de contrato (sem dealId pré-selecionado) */}
      {currentTenant?.id && (
        <ContractWizardDialog
          open={wizardOpen}
          onOpenChange={(open) => {
            setWizardOpen(open);
            if (!open) {
              // Atualiza listagem ao fechar wizard
              queryClient.invalidateQueries({ queryKey: ['contracts-paginated'] });
              queryClient.invalidateQueries({ queryKey: ['contract-list-kpis'] });
            }
          }}
          tenantId={currentTenant.id}
        />
      )}
    </div>
  );
}
